/**
 * Floating Edge Utilities for the simplified graph explorer.
 *
 * Calculates dynamic connection points between nodes based on their
 * relative positions, so edges connect at the optimal points on node boundaries.
 *
 * Ported from the CypherVisualizer reference implementation.
 */

import { Position, type InternalNode } from '@xyflow/react';

/**
 * Get the center point of a node.
 */
function getNodeCenter(node: InternalNode): { x: number; y: number } {
	const width = node.measured?.width ?? node.width ?? 160;
	const height = node.measured?.height ?? node.height ?? 32;

	return {
		x: node.internals.positionAbsolute.x + width / 2,
		y: node.internals.positionAbsolute.y + height / 2,
	};
}

/**
 * Calculate where a line from nodeCenter to targetCenter intersects
 * the boundary of the node.
 */
function getNodeIntersection(
	nodeCenter: { x: number; y: number },
	targetCenter: { x: number; y: number },
	nodePosition: { x: number; y: number },
	nodeWidth: number,
	nodeHeight: number,
): { x: number; y: number; position: Position } {
	const dx = targetCenter.x - nodeCenter.x;
	const dy = targetCenter.y - nodeCenter.y;

	// Handle the case where nodes are at the same position
	if (dx === 0 && dy === 0) {
		return {
			x: nodeCenter.x,
			y: nodeCenter.y,
			position: Position.Right,
		};
	}

	const halfWidth = nodeWidth / 2;
	const halfHeight = nodeHeight / 2;

	const angle = Math.atan2(dy, dx);
	const cornerAngle = Math.atan2(halfHeight, halfWidth);

	let x: number;
	let y: number;
	let position: Position;

	if (Math.abs(angle) < cornerAngle) {
		// Right edge
		x = nodePosition.x + nodeWidth;
		y = nodeCenter.y + halfWidth * Math.tan(angle);
		position = Position.Right;
	} else if (Math.abs(angle) > Math.PI - cornerAngle) {
		// Left edge
		x = nodePosition.x;
		y = nodeCenter.y - halfWidth * Math.tan(angle);
		position = Position.Left;
	} else if (angle > 0) {
		// Bottom edge
		x = nodeCenter.x + halfHeight / Math.tan(angle);
		y = nodePosition.y + nodeHeight;
		position = Position.Bottom;
	} else {
		// Top edge
		x = nodeCenter.x - halfHeight / Math.tan(angle);
		y = nodePosition.y;
		position = Position.Top;
	}

	return { x, y, position };
}

/**
 * Get the parameters needed to draw an edge between two nodes.
 * Returns the intersection points on the node boundaries.
 */
export function getEdgeParams(
	source: InternalNode,
	target: InternalNode,
): {
	sx: number;
	sy: number;
	tx: number;
	ty: number;
	sourcePos: Position;
	targetPos: Position;
} {
	const sourceCenter = getNodeCenter(source);
	const targetCenter = getNodeCenter(target);

	const sourceWidth = source.measured?.width ?? source.width ?? 160;
	const sourceHeight = source.measured?.height ?? source.height ?? 32;
	const targetWidth = target.measured?.width ?? target.width ?? 160;
	const targetHeight = target.measured?.height ?? target.height ?? 32;

	const sourcePos = source.internals.positionAbsolute;
	const targetPos = target.internals.positionAbsolute;

	const {
		x: sx,
		y: sy,
		position: sourcePosition,
	} = getNodeIntersection(sourceCenter, targetCenter, sourcePos, sourceWidth, sourceHeight);

	const {
		x: tx,
		y: ty,
		position: targetPosition,
	} = getNodeIntersection(targetCenter, sourceCenter, targetPos, targetWidth, targetHeight);

	return {
		sx,
		sy,
		tx,
		ty,
		sourcePos: sourcePosition,
		targetPos: targetPosition,
	};
}
